/**
 * wonderWorldProgress.js
 *
 * Formal data contract for WonderWorld's localStorage persistence.
 *
 * Previously: ad-hoc JSON written inline in a useEffect inside App.js.
 * Now: typed schema with load/save helpers and derived-unlock calculation
 * all in one place. Adding a new field = one edit here, nowhere else.
 *
 * Storage keys:
 *   wonderworld_progress  — game state snapshot
 *   wonderworld_history   — session history array (up to 20 entries)
 *   wonderworld_feedback  — parent feedback log
 */

import { worldOrder } from "./data/wonderWorldData";

// ─── Storage key constants ────────────────────────────────────────────────────

export const STORAGE_KEY_PROGRESS = "wonderworld_progress";
export const STORAGE_KEY_HISTORY = "wonderworld_history";
export const STORAGE_KEY_FEEDBACK = "wonderworld_feedback";

// ─── Schema defaults ──────────────────────────────────────────────────────────

/**
 * Default (empty) progress state.
 * Any field added here is automatically handled by loadProgress.
 */
export const DEFAULT_PROGRESS = {
  lang: "en",
  page: "home",
  world: null,
  node: "start",
  completed: [],
  unlockedWorlds: [worldOrder[0]],
  stars: 0,
  score: 0,
  difficulty: "easy",
  decisionLog: [],
  questionCount: 0,
  correctCount: 0,
  sessionStart: null,           // null → will be set to Date.now() on first mount
  history: [],
  lastQuestionType: null,
  askedQuestionKeys: [],
  levelState: 1,
  pendingBossQueue: [],
  usedQuestionChoiceKeys: [],
  pendingStoryNode: null,
  question: null,
  lastResultKind: "normal",
  showFinalEndingAfterSummary: false
};

/**
 * Session history entry schema (up to 20 kept in wonderworld_history).
 *
 * @typedef {Object} SessionEntry
 * @property {number}  id
 * @property {string}  world
 * @property {number}  stars
 * @property {number}  questionCount
 * @property {number}  correctCount
 * @property {number}  accuracyRate       — percentage, 0-100
 * @property {number}  exploration
 * @property {number}  analysis
 * @property {number}  risk
 * @property {number}  planning
 * @property {number}  curiosity
 * @property {number}  care
 * @property {number}  logic
 * @property {number}  cooperation
 * @property {number}  observation
 * @property {number}  efficiency
 * @property {number}  creativity
 * @property {string}  createdAt
 */

// ─── Storage helpers ──────────────────────────────────────────────────────────

function readJsonFromStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Loads saved progress from localStorage and derives unlocked worlds.
 * Returns a plain object matching DEFAULT_PROGRESS shape.
 */
export function loadProgress() {
  const saved = readJsonFromStorage(STORAGE_KEY_PROGRESS, {});

  // Validate and coerce each field against the schema.
  const completed = Array.isArray(saved.completed)
    ? saved.completed.filter((w) => worldOrder.includes(w))
    : [];

  const savedUnlocked = Array.isArray(saved.unlockedWorlds)
    ? saved.unlockedWorlds.filter((w) => worldOrder.includes(w))
    : [];

  // Derive unlocked worlds from completed set — so saves with partial data
  // don't soft-lock a player who completed multiple worlds.
  let derivedUnlockIndex = 0;
  while (
    derivedUnlockIndex < worldOrder.length - 1 &&
    completed.includes(worldOrder[derivedUnlockIndex])
  ) {
    derivedUnlockIndex += 1;
  }
  const derivedUnlocked = worldOrder.slice(0, derivedUnlockIndex + 1);
  const unlockedWorlds = Array.from(
    new Set([worldOrder[0], ...savedUnlocked, ...derivedUnlocked])
  );

  return {
    lang: ["en", "fr", "zh"].includes(saved.lang) ? saved.lang : DEFAULT_PROGRESS.lang,
    page: typeof saved.page === "string" ? saved.page : DEFAULT_PROGRESS.page,
    world: typeof saved.world === "string" && worldOrder.includes(saved.world) ? saved.world : null,
    node: typeof saved.node === "string" ? saved.node : DEFAULT_PROGRESS.node,
    completed,
    unlockedWorlds,
    stars: typeof saved.stars === "number" ? saved.stars : DEFAULT_PROGRESS.stars,
    score: typeof saved.score === "number" ? saved.score : DEFAULT_PROGRESS.score,
    difficulty: ["easy", "medium", "hard"].includes(saved.difficulty)
      ? saved.difficulty
      : DEFAULT_PROGRESS.difficulty,
    decisionLog: Array.isArray(saved.decisionLog) ? saved.decisionLog : [],
    questionCount: typeof saved.questionCount === "number" ? saved.questionCount : 0,
    correctCount: typeof saved.correctCount === "number" ? saved.correctCount : 0,
    sessionStart: typeof saved.sessionStart === "number" ? saved.sessionStart : Date.now(),
    lastQuestionType: typeof saved.lastQuestionType === "string" ? saved.lastQuestionType : null,
    askedQuestionKeys: Array.isArray(saved.askedQuestionKeys) ? saved.askedQuestionKeys : [],
    levelState: typeof saved.levelState === "number" ? saved.levelState : 1,
    pendingBossQueue: Array.isArray(saved.pendingBossQueue) ? saved.pendingBossQueue : [],
    usedQuestionChoiceKeys: Array.isArray(saved.usedQuestionChoiceKeys)
      ? saved.usedQuestionChoiceKeys
      : [],
    pendingStoryNode:
      typeof saved.pendingStoryNode === "string" ? saved.pendingStoryNode : null,
    question:
      saved.question && typeof saved.question === "object" ? saved.question : null,
    lastResultKind:
      typeof saved.lastResultKind === "string"
        ? saved.lastResultKind
        : DEFAULT_PROGRESS.lastResultKind,
    showFinalEndingAfterSummary: saved.showFinalEndingAfterSummary === true
  };
}

/**
 * Loads session history from localStorage.
 * @returns {SessionEntry[]}
 */
export function loadHistory() {
  const raw = readJsonFromStorage(STORAGE_KEY_HISTORY, []);
  return Array.isArray(raw) ? raw : [];
}

/**
 * Saves the given progress snapshot to localStorage (debounced by caller).
 * Accepts a partial object — only keys present in DEFAULT_PROGRESS are written.
 */
export function saveProgress(state) {
  try {
    localStorage.setItem(STORAGE_KEY_PROGRESS, JSON.stringify({
      lang: state.lang,
      page: state.page,
      world: state.world,
      node: state.node,
      completed: state.completed,
      unlockedWorlds: state.unlockedWorlds,
      stars: state.stars,
      score: state.score,
      difficulty: state.difficulty,
      decisionLog: state.decisionLog,
      questionCount: state.questionCount,
      correctCount: state.correctCount,
      sessionStart: state.sessionStart,
      lastQuestionType: state.lastQuestionType,
      askedQuestionKeys: state.askedQuestionKeys,
      levelState: state.levelState,
      pendingBossQueue: state.pendingBossQueue,
      usedQuestionChoiceKeys: state.usedQuestionChoiceKeys,
      pendingStoryNode: state.pendingStoryNode,
      question: state.question,
      lastResultKind: state.lastResultKind,
      showFinalEndingAfterSummary: state.showFinalEndingAfterSummary
    }));
  } catch {
    // Silently ignore storage quota errors.
  }
}

/**
 * Saves the session history array.
 */
export function saveHistory(history) {
  try {
    localStorage.setItem(STORAGE_KEY_HISTORY, JSON.stringify(history));
  } catch {
    // Silently ignore storage quota errors.
  }
}

/**
 * Clears both progress and history from localStorage.
 * Optionally keeps progress (lang, unlockedWorlds) intact.
 */
export function clearHistory() {
  try {
    localStorage.removeItem(STORAGE_KEY_HISTORY);
  } catch {
    // Ignore.
  }
}
